import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Select,
  Combobox,
  type ComboboxOption,
  StatePanel,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  V2Skeleton,
  V2Tabs,
  V2ToastStack,
  type V2ToastItem,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { exactWhen, relativeWhen } from '../../lib/date/relativeWhen.js';
import type {
  CommentListResponse,
  ProjectComment,
  ProjectListResponse,
  ProjectTaskListResponse,
} from '../../lib/types/projects.js';
import './project-reminders.css';

type TargetType = 'project' | 'task';

const TOAST_MS = 5000;

const fa = (n: number) => n.toLocaleString('fa-IR');

function parseType(value: string | null): TargetType {
  return value === 'task' ? 'task' : 'project';
}

/**
 * UI_REFERENCE_SOURCE: no dedicated Design Board — follows the design
 * system's own primitives directly, per the route inventory's PO decision.
 * canonical pattern: pick a target (project/task), read its comments, post a
 * new one. The backend endpoint is target-scoped, not global, so nothing is
 * listed until a target is chosen. A link may preselect it with
 * `?targetType=project|task&targetRef=<ref>`.
 */
export function ProjectCommentsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [searchParams, setSearchParams] = useSearchParams();

  const targetType = parseType(searchParams.get('targetType'));
  const targetRef = searchParams.get('targetRef') ?? '';

  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const idempotencyKey = useRef(crypto.randomUUID());

  const projects = useApiGet<ProjectListResponse>(
    slug && targetType === 'project' ? `${base}/api/v1/projects?limit=200` : null,
    [slug, targetType],
  );
  const tasks = useApiGet<ProjectTaskListResponse>(
    slug && targetType === 'task' ? `${base}/api/v1/projects/tasks?limit=200` : null,
    [slug, targetType],
  );

  const targetOptions = useMemo(() => {
    if (targetType === 'project') {
      return projects.status === 'ready'
        ? projects.data.projects.map((p) => ({ ref: p.ref, label: `${p.name} (${p.ref})` }))
        : [];
    }
    return tasks.status === 'ready'
      ? tasks.data.tasks.map((t) => ({ ref: t.ref, label: `${t.title} (${t.ref})` }))
      : [];
  }, [targetType, projects, tasks]);

  const targetComboboxOptions = useMemo<ComboboxOption[]>(() => {
    return targetOptions.map((o) => ({ value: o.ref, label: o.label }));
  }, [targetOptions]);

  const res = useApiGet<CommentListResponse>(
    slug && targetRef
      ? `${base}/api/v1/projects/comments?targetType=${targetType}&targetRef=${encodeURIComponent(targetRef)}`
      : null,
    [slug, targetType, targetRef],
  );
  // Keep the thread on screen while it reloads after posting.
  // The key ties the list to its target, so back/forward never shows another target's thread.
  const targetKey = `${targetType}:${targetRef}`;
  const fetched = res.status === 'ready' ? res.data.comments : null;
  const [loaded, setLoaded] = useState<{
    readonly key: string;
    readonly list: readonly ProjectComment[];
  } | null>(null);
  useEffect(() => {
    if (fetched) setLoaded({ key: targetKey, list: fetched });
  }, [fetched, targetKey]);
  const comments: readonly ProjectComment[] | null = loaded?.key === targetKey ? loaded.list : null;

  const [toasts, setToasts] = useState<readonly V2ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: string) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  const toast = useCallback(
    (message: string, tone: NonNullable<V2ToastItem['tone']>) => {
      seq.current += 1;
      const id = `t${seq.current}`;
      setToasts((p) => [...p, { id, message, tone }]);
      window.setTimeout(() => dismiss(id), TOAST_MS);
    },
    [dismiss],
  );

  if (!slug) return null;

  function setTarget(type: TargetType, ref: string) {
    setBody('');
    setSearchParams(ref ? { targetType: type, targetRef: ref } : { targetType: type });
  }

  async function onPost(event?: FormEvent) {
    event?.preventDefault();
    const text = body.trim();
    if (!targetRef || !text || submitting) return;
    setSubmitting(true);
    try {
      await apiPost(
        `${base}/api/v1/projects/comments`,
        { targetType, targetRef, body: text },
        { headers: { 'idempotency-key': idempotencyKey.current } },
      );
      idempotencyKey.current = crypto.randomUUID();
      setBody('');
      res.reload();
    } catch {
      toast('ثبت نظر ناموفق بود.', 'danger');
    } finally {
      setSubmitting(false);
    }
  }

  const optionsLoading =
    targetType === 'project' ? projects.status === 'loading' : tasks.status === 'loading';
  const optionsError =
    targetType === 'project' ? projects.status === 'error' : tasks.status === 'error';

  let thread;
  if (!targetRef) {
    thread = (
      <V2EmptyState
        compact
        icon="info"
        title="یک پروژه یا کار را انتخاب کنید."
        description="نظرات هر پروژه یا کار جداگانه نگهداری می‌شود."
      />
    );
  } else if (comments === null && res.status === 'error') {
    thread = (
      <StatePanel
        variant={res.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={res.error.status === 403 ? 'دسترسی ندارید' : 'نظرات قابل نمایش نیست'}
      />
    );
  } else if (comments === null) {
    thread = (
      <div aria-busy="true" aria-live="polite">
        {[0, 1].map((i) => (
          <div key={i} className="prj-cmt__item">
            <V2Skeleton height={12} />
            <V2Skeleton width={90} height={10} />
          </div>
        ))}
      </div>
    );
  } else if (comments.length === 0) {
    thread = (
      <V2EmptyState
        compact
        icon="info"
        title="هنوز نظری ثبت نشده است."
        description="اولین نظر را بنویسید."
      />
    );
  } else {
    thread = comments.map((c) => (
      <div key={c.ref} className="prj-cmt__item">
        <p className="prj-cmt__body">{c.body}</p>
        <span className="prj-cmt__when" title={exactWhen(c.createdAt)}>
          {relativeWhen(c.createdAt)}
        </span>
      </div>
    ));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card clipped>
        <V2CardHeader
          icon="info"
          title="نظرات"
          subtitle={comments ? `${fa(comments.length)} نظر` : undefined}
        />
        <div className="prj-cmt__target">
          <V2Tabs
            items={[
              { id: 'project', label: 'پروژه' },
              { id: 'task', label: 'کار' },
            ]}
            activeId={targetType}
            onChange={(id) => setTarget(id as TargetType, '')}
          />
          <div style={{ flex: 1, minWidth: 260 }}>
            <Combobox
              options={targetComboboxOptions}
              value={targetRef}
              onValueChange={(val) => setTarget(targetType, val)}
              placeholder={
                optionsLoading
                  ? 'در حال بارگذاری…'
                  : optionsError
                    ? 'فهرست در دسترس نیست'
                    : targetType === 'project'
                      ? 'انتخاب یا جست‌وجوی پروژه…'
                      : 'انتخاب یا جست‌وجوی کار (مثال: TSK-0001)…'
              }
              disabled={optionsLoading || optionsError}
            />
          </div>
        </div>

        {thread}

        {targetRef ? (
          <form className="prj-cmt__composer" onSubmit={onPost}>
            <textarea
              className="sk-textarea"
              rows={3}
              value={body}
              aria-label="متن نظر"
              placeholder="نظر خود را بنویسید…"
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void onPost();
              }}
            />
            <div className="prj-cmt__composer-foot">
              <span className="prj-cmt__hint">Ctrl + Enter برای ثبت</span>
              <V2Button type="submit" variant="primary" disabled={submitting || body.trim() === ''}>
                {submitting ? 'در حال ثبت…' : 'ثبت نظر'}
              </V2Button>
            </div>
          </form>
        ) : null}
      </V2Card>
      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
